// DepartmentConfigPage.js
import React, { useState, useEffect } from "react";
import {
  FormGroup,
  Input,
  Label,
  Button,
  Alert,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import API from "../../services/API";

const weekdays = ["Isnin", "Selasa", "Rabu", "Khamis", "Jumaat"];
const weekdayApiIndexes = [0, 1, 2, 3, 4];

const DepartmentConfigPage = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [mcReasons, setMCReasons] = useState([]);
  const [loadingMC, setLoadingMC] = useState(true);
  const [mcError, setMCError] = useState(null);
  const [selectedMC, setSelectedMC] = useState("");

  const [activationDate, setActivationDate] = useState("");
  const [loadingActivation, setLoadingActivation] = useState(true);
  const [activationLocked, setActivationLocked] = useState(false);

  const [hasAfternoonSession, setHasAfternoonSession] = useState(false);

  const [mcLetterEnabled, setMcLetterEnabled] = useState(true);
  const [loadingMCLetter, setLoadingMCLetter] = useState(true);
  const [savingMCLetter, setSavingMCLetter] = useState(false);

  const [timetableEnabled, setTimetableEnabled] = useState(false);
  const [loadingTimetable, setLoadingTimetable] = useState(true);
  const [savingTimetable, setSavingTimetable] = useState(false);

  useEffect(() => {
    fetchConfig();
    getMCReasons();
    fetchDefaultMC();
    fetchActivationDate();
    fetchMCLetterConfig();
    fetchTimetableConfig();
  }, []);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  const fetchTimetableConfig = async () => {
    setLoadingTimetable(true);

    try {
      const res = await API.get("/timetable/enabled", {
        headers: getHeaders(),
      });

      if (res.data.response_code === 1000) {
        setTimetableEnabled(!!res.data.data.enabled);
      } else {
        toast.error(res.data.message || "Gagal memuatkan tetapan jadual");
      }
    } catch (err) {
      console.log(err);
      toast.error("Ralat semasa memuatkan tetapan jadual");
    } finally {
      setLoadingTimetable(false);
    }
  };

  const saveTimetableConfig = async (enabled) => {
    setSavingTimetable(true);

    try {
      const res = await API.post(
        "/timetable/enabled",
        { enabled },
        { headers: getHeaders() }
      );

      if (res.data.response_code === 1000) {
        setTimetableEnabled(enabled);
        toast.success("Tetapan jadual berjaya dikemaskini");
      } else {
        toast.error(res.data.message || "Gagal menyimpan tetapan jadual");
      }
    } catch (err) {
      toast.error("Ralat semasa mengemaskini tetapan jadual");
    } finally {
      setSavingTimetable(false);
    }
  };

  const fetchMCLetterConfig = async () => {
    setLoadingMCLetter(true);

    try {
      const res = await API.get("/portal/attendance/mcLetterConfig", {
        headers: getHeaders(),
      });

      if (res.data.response_code === 1000 && res.data.data) {
        setMcLetterEnabled(!!res.data.data.mc_letter_enabled);
      } else {
        toast.error(res.data.message || "Gagal memuatkan tetapan dokumen MC");
      }
    } catch (err) {
      toast.error("Ralat semasa memuatkan tetapan dokumen MC");
    } finally {
      setLoadingMCLetter(false);
    }
  };

  const saveMCLetterConfig = async (enabled) => {
    setSavingMCLetter(true);

    try {
      const res = await API.post(
        "/portal/attendance/mcLetterConfig",
        { mc_letter_enabled: enabled },
        { headers: getHeaders() }
      );

      if (res.data.response_code === 1000) {
        setMcLetterEnabled(enabled);
        toast.success("Tetapan dokumen MC berjaya dikemaskini");
      } else {
        toast.error(res.data.message || "Gagal mengemaskini tetapan dokumen MC");
      }
    } catch (err) {
      toast.error("Ralat semasa mengemaskini tetapan dokumen MC");
    } finally {
      setSavingMCLetter(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);

    try {
      const morningRes = await API.get("/portal/attendance/config", {
        headers: getHeaders(),
      });

      let morningData = {};

      if (morningRes.data.response_code === 1000) {
        morningData = morningRes.data.data || {};
      }

      let merged = { ...morningData };
      let hasAfternoon = false;

      try {
        const afternoonRes = await API.get(
          "/portal/attendance/afternoonConfig",
          { headers: getHeaders() }
        );

        if (afternoonRes.data.response_code === 1000) {
          const afternoonData = afternoonRes.data.data || {};
          const defaults = afternoonData.default || {};

          weekdayApiIndexes.forEach((dayIndex) => {
            const perDay = afternoonData[String(dayIndex)] || {};
            const mergedDay = { ...defaults, ...perDay };

            Object.entries(mergedDay).forEach(([key, value]) => {
              merged[`${key}_${dayIndex}`] = value;
            });
          });

          hasAfternoon = true;
        } else if (afternoonRes.data.response_code === 4004) {
          hasAfternoon = false;
        }
      } catch (err) {
        console.log("No afternoon session available or API error:", err);
        hasAfternoon = false;
      }

      setHasAfternoonSession(hasAfternoon);
      setConfig(merged);

      if (morningData.mc_category) {
        setSelectedMC(
          `${morningData.mc_category}|||${morningData.mc_sub_category || ""}`
        );
      }
    } catch (err) {
      setError("Gagal memuatkan tetapan kehadiran");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivationDate = async () => {
    setLoadingActivation(true);

    try {
      const res = await API.get("/portal/schoolActivation", {
        headers: getHeaders(),
      });

      if (res.data.response_code === 1000 && res.data.data?.activation_date) {
        setActivationDate(res.data.data.activation_date.split("T")[0]);
        setActivationLocked(true);
      } else {
        setActivationLocked(false);
      }
    } catch {
      toast.error("Gagal memuatkan tarikh aktif jabatan");
    } finally {
      setLoadingActivation(false);
    }
  };

  const saveActivationDate = async () => {
    if (!activationDate) {
      toast.error("Sila pilih tarikh aktif jabatan");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selected = new Date(activationDate);
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(today.getFullYear() + 1);

    if (selected < today) {
      toast.error("Tarikh aktif tidak boleh sebelum hari ini");
      return;
    }

    if (selected > oneYearLater) {
      toast.error("Tarikh aktif tidak boleh melebihi 1 tahun dari sekarang");
      return;
    }

    setSaving(true);

    try {
      const res = await API.post(
        "/portal/schoolActivation",
        { activation_date: activationDate },
        { headers: getHeaders() }
      );

      if (res.data.response_code === 1000) {
        toast.success("Tarikh aktif jabatan berjaya dikemaskini");
        setActivationLocked(true);
      } else {
        toast.error(res.data.message || "Gagal mengemaskini tarikh aktif");
      }
    } catch (err) {
      toast.error("Ralat semasa menyimpan tarikh aktif");
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async (isAfternoon = false) => {
    setSaving(true);

    try {
      const payloads = [];

      if (!isAfternoon) {
        ["early_cutoff", "late_after"].forEach((field) => {
          const value = config[field];

          if (value && value.includes(":")) {
            const [hour, minute] = value.split(":").map(Number);

            payloads.push(
              API.post(
                "/portal/attendance/config",
                {
                  updated_field: field,
                  value,
                  hour,
                  minute,
                },
                { headers: getHeaders() }
              )
            );
          }
        });
      } else {
        weekdayApiIndexes.forEach((dayIndex) => {
          ["early_cutoff_afternoon", "late_after_afternoon"].forEach(
            (field) => {
              const key = `${field}_${dayIndex}`;
              const value = config[key];

              if (value && value.includes(":")) {
                const [hour, minute] = value.split(":").map(Number);

                payloads.push(
                  API.post(
                    "/portal/attendance/afternoonConfig",
                    {
                      updated_field: field,
                      value,
                      hour,
                      minute,
                      day_of_week: Number(dayIndex),
                    },
                    { headers: getHeaders() }
                  )
                );
              }
            }
          );
        });
      }

      if (payloads.length === 0) {
        toast.error("Tiada masa untuk disimpan");
        return;
      }

      const results = await Promise.all(payloads);

      results.forEach((res) => {
        if (res.data?.response_code !== 1000) {
          throw new Error(res.data?.message || "Simpan gagal");
        }
      });

      toast.success(
        isAfternoon
          ? "Tetapan kehadiran petang berjaya disimpan"
          : "Tetapan kehadiran pagi berjaya disimpan"
      );

      fetchConfig();
    } catch (err) {
      console.error("Save error", err);
      setError("Gagal menyimpan tetapan");
      toast.error("Gagal menyimpan tetapan");
    } finally {
      setSaving(false);
    }
  };

  const getMCReasons = async () => {
    setLoadingMC(true);

    try {
      const response = await fetch(
        "https://api.schoolpay.tech/attendance/absenceReason"
      );
      const resJSON = await response.json();

      if (resJSON.response_code === 1000) {
        setMCReasons(resJSON.data || []);
      } else {
        setMCError(resJSON.message || "Gagal memuatkan sebab MC");
      }
    } catch {
      setMCError("Gagal memuatkan sebab MC");
    } finally {
      setLoadingMC(false);
    }
  };

  const fetchDefaultMC = async () => {
    try {
      const res = await API.get("/portal/attendance/mcConfig", {
        headers: getHeaders(),
      });

      if (res.data.response_code === 1000) {
        const category = res.data.data.category || "";
        const subCategory = res.data.data.sub_category || "";

        setConfig((cfg) => ({
          ...cfg,
          mc_category: category,
          mc_sub_category: subCategory,
        }));

        if (category) {
          setSelectedMC(`${category}|||${subCategory}`);
        }
      }
    } catch {
      setError("Gagal memuatkan tetapan sebab MC");
    }
  };

  const saveDefaultMC = async (e) => {
    e.preventDefault();

    if (!selectedMC) {
      toast.error("Sila pilih sebab MC / tidak hadir");
      return;
    }

    const [mc_category, mc_sub_category] = selectedMC.split("|||");

    setSaving(true);

    try {
      const res = await API.post(
        "/portal/attendance/mcConfig",
        {
          category: mc_category,
          sub_category: mc_sub_category,
        },
        { headers: getHeaders() }
      );

      if (res.data.response_code === 1000) {
        toast.success("Sebab MC berjaya disimpan");

        setConfig((cfg) => ({
          ...cfg,
          mc_category,
          mc_sub_category,
        }));
      } else {
        setError(res.data.message || "Gagal menyimpan sebab MC");
      }
    } catch {
      setError("Gagal menyimpan sebab MC");
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ enabled, loading, saving, onToggle }) => {
    if (loading) return <Spinner size="sm" />;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          onClick={() => {
            if (!saving) onToggle();
          }}
          style={{
            width: 54,
            height: 30,
            borderRadius: 999,
            padding: 3,
            background: enabled ? "#0B4C9C" : "#d1d5db",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.65 : 1,
            transition: "all 0.2s ease",
            boxShadow: enabled
              ? "0 8px 18px rgba(11, 76, 156, 0.28)"
              : "inset 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "#fff",
              transform: enabled ? "translateX(24px)" : "translateX(0)",
              transition: "transform 0.2s ease",
              boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
            }}
          />
        </div>

        <div>
          <div
            style={{
              fontWeight: 800,
              color: enabled ? "#0B4C9C" : "#6b7280",
              lineHeight: 1,
            }}
          >
            {enabled ? "Aktif" : "Tidak Aktif"}
          </div>

          {saving && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
              Sedang menyimpan...
            </div>
          )}
        </div>
      </div>
    );
  };

  const SectionCard = ({ title, subtitle, badge, children }) => (
    <Card style={sectionCardStyle}>
      <CardHeader style={sectionHeaderStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
              {title}
            </div>

            {subtitle && <div style={sectionSubtitleStyle}>{subtitle}</div>}
          </div>

          {badge && <span style={badgeStyle}>{badge}</span>}
        </div>
      </CardHeader>

      <CardBody style={{ padding: 24 }}>{children}</CardBody>
    </Card>
  );

  const SettingRow = ({ title, description, right }) => (
    <div style={settingRowStyle}>
      <div style={{ maxWidth: 720 }}>
        <div style={{ fontSize: 15, fontWeight: 850, color: "#111827" }}>
          {title}
        </div>

        {description && <div style={settingDescriptionStyle}>{description}</div>}
      </div>

      <div style={{ flex: "0 0 auto" }}>{right}</div>
    </div>
  );

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={heroStyle}>
          <div style={heroGlowStyle} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={heroBadgeStyle}>Tetapan Sistem Jabatan</div>

            <h1 style={heroTitleStyle}>Tetapan Kehadiran</h1>

            <p style={heroDescriptionStyle}>
              Urus masa kehadiran, tetapan MC, jadual bertugas dan tarikh aktif
              jabatan dalam satu tempat.
            </p>
          </div>
        </div>

        {error && (
          <Alert color="danger" style={{ borderRadius: 16 }}>
            {error}
          </Alert>
        )}

        <div style={layoutGridStyle}>
          <div>
            <SectionCard
              title="Kehadiran Sesi Pagi"
              subtitle="Tetapkan masa utama untuk menentukan daftar masuk awal dan lewat."
              badge="Utama"
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spinner />
                </div>
              ) : (
                <>
                  <div style={twoColumnGridStyle}>
                    <FormGroup>
                      <Label style={labelStyle}>Daftar Masuk Awal Sebelum</Label>
                      <Input
                        type="time"
                        value={config.early_cutoff || ""}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            early_cutoff: e.target.value,
                          })
                        }
                        style={inputStyle}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label style={labelStyle}>Dikira Lewat Selepas</Label>
                      <Input
                        type="time"
                        value={config.late_after || ""}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            late_after: e.target.value,
                          })
                        }
                        style={inputStyle}
                      />
                    </FormGroup>
                  </div>

                  <Button
                    disabled={saving}
                    onClick={() => saveAll(false)}
                    style={primaryButtonStyle}
                  >
                    {saving ? "Sedang Menyimpan..." : "Simpan Tetapan Pagi"}
                  </Button>
                </>
              )}
            </SectionCard>

            {hasAfternoonSession && (
              <SectionCard
                title="Kehadiran Sesi Petang"
                subtitle="Tetapkan masa kehadiran sesi petang mengikut hari bekerja."
                badge="Sesi Petang"
              >
                <div style={tableWrapStyle}>
                  <Table responsive hover style={{ marginBottom: 0 }}>
                    <thead style={{ background: "#eff6ff" }}>
                      <tr>
                        <th style={tableHeadStyle}>Hari</th>
                        <th style={tableHeadStyle}>
                          Daftar Masuk Awal Sebelum
                        </th>
                        <th style={tableHeadStyle}>Dikira Lewat Selepas</th>
                      </tr>
                    </thead>

                    <tbody>
                      {weekdays.map((day, idx) => {
                        const apiIndex = weekdayApiIndexes[idx];

                        return (
                          <tr key={day}>
                            <td style={{ fontWeight: 800, color: "#0f172a" }}>
                              {day}
                            </td>
                            <td>
                              <Input
                                type="time"
                                value={
                                  config[
                                    `early_cutoff_afternoon_${apiIndex}`
                                  ] || ""
                                }
                                onChange={(e) =>
                                  setConfig({
                                    ...config,
                                    [`early_cutoff_afternoon_${apiIndex}`]:
                                      e.target.value,
                                  })
                                }
                                style={inputStyle}
                              />
                            </td>
                            <td>
                              <Input
                                type="time"
                                value={
                                  config[`late_after_afternoon_${apiIndex}`] ||
                                  ""
                                }
                                onChange={(e) =>
                                  setConfig({
                                    ...config,
                                    [`late_after_afternoon_${apiIndex}`]:
                                      e.target.value,
                                  })
                                }
                                style={inputStyle}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <Button
                  disabled={saving}
                  onClick={() => saveAll(true)}
                  style={{ ...primaryButtonStyle, marginTop: 18 }}
                >
                  {saving ? "Sedang Menyimpan..." : "Simpan Tetapan Petang"}
                </Button>
              </SectionCard>
            )}

            <SectionCard
              title="Tetapan MC & Tidak Hadir"
              subtitle="Tetapkan sebab MC lalai dan kawal sama ada dokumen sokongan perlu dimuat naik."
              badge="Kesihatan"
            >
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>
                  Sebab MC / Tidak Hadir Lalai
                </div>

                {mcError && (
                  <Alert
                    color="danger"
                    style={{ marginTop: 12, borderRadius: 14 }}
                  >
                    {mcError}
                  </Alert>
                )}

                {loadingMC ? (
                  <Spinner size="sm" style={{ marginTop: 14 }} />
                ) : (
                  <form onSubmit={saveDefaultMC} style={mcFormGridStyle}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label style={labelStyle}>Pilih Sebab Lalai</Label>
                      <Input
                        type="select"
                        value={selectedMC}
                        onChange={(e) => setSelectedMC(e.target.value)}
                        style={inputStyle}
                      >
                        <option value="">-- Sila pilih --</option>
                        {mcReasons.map((reason) => (
                          <option
                            key={reason.reason_id}
                            value={`${reason.name}|||${
                              reason.sub_reason || ""
                            }`}
                          >
                            {reason.name}
                            {reason.sub_reason
                              ? ` — ${reason.sub_reason}`
                              : ""}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    <Button
                      disabled={saving}
                      type="submit"
                      style={successButtonStyle}
                    >
                      Simpan Sebab Lalai
                    </Button>
                  </form>
                )}

                {config.mc_category && (
                  <div style={savedBoxStyle}>
                    Sebab disimpan: <b>{config.mc_category}</b>
                    {config.mc_sub_category
                      ? ` — ${config.mc_sub_category}`
                      : ""}
                  </div>
                )}
              </div>

              <SettingRow
                title="Wajib Muat Naik Dokumen MC"
                description="Jika diaktifkan, kakitangan perlu memuat naik dokumen MC untuk rekod cuti sakit."
                right={
                  <ToggleSwitch
                    enabled={mcLetterEnabled}
                    loading={loadingMCLetter}
                    saving={savingMCLetter}
                    onToggle={() => saveMCLetterConfig(!mcLetterEnabled)}
                  />
                }
              />
            </SectionCard>
          </div>

          <div>
            <SectionCard
              title="Automasi"
              subtitle="Kawal fungsi automatik yang digunakan oleh jabatan."
              badge="Automasi"
            >
              <SettingRow
                title="Aktifkan Jadual Bertugas"
                description="Benarkan pengguna mengakses fungsi jadual bertugas dalam aplikasi."
                right={
                  <ToggleSwitch
                    enabled={timetableEnabled}
                    loading={loadingTimetable}
                    saving={savingTimetable}
                    onToggle={() => saveTimetableConfig(!timetableEnabled)}
                  />
                }
              />
            </SectionCard>

            <SectionCard
              title="Tarikh Aktif Jabatan"
              subtitle="Tetapkan bila sistem jabatan mula digunakan secara rasmi."
              badge={activationLocked ? "Dikunci" : "Boleh Edit"}
            >
              {loadingActivation ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FormGroup>
                    <Label style={labelStyle}>Tarikh Aktif</Label>
                    <Input
                      type="date"
                      value={activationDate || ""}
                      onChange={(e) => setActivationDate(e.target.value)}
                      disabled={activationLocked}
                      style={inputStyle}
                    />
                  </FormGroup>

                  <Button
                    disabled={saving || activationLocked}
                    onClick={saveActivationDate}
                    style={successButtonStyle}
                  >
                    Simpan Tarikh Aktif
                  </Button>

                  {activationDate && (
                    <div
                      style={{
                        ...savedBoxStyle,
                        background: activationLocked ? "#f8fafc" : "#eff6ff",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        Tarikh ditetapkan: {activationDate}
                      </div>
                      <div style={settingDescriptionStyle}>
                        {activationLocked
                          ? "Hubungi pentadbir sistem jika perlu menukar tarikh aktif."
                          : "Tarikh ini akan digunakan untuk mengaktifkan sistem jabatan."}
                      </div>
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentConfigPage;

const pageStyle = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)",
  padding: "28px 24px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #0B4C9C 0%, #0F65C9 55%, #1E88E5 100%)",
  borderRadius: 28,
  padding: "34px 36px",
  marginBottom: 24,
  color: "#fff",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 24px 70px rgba(11, 76, 156, 0.22)",
};

const heroGlowStyle = {
  position: "absolute",
  right: -80,
  top: -80,
  width: 260,
  height: 260,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.11)",
};

const heroBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(255,255,255,0.14)",
  border: "1px solid rgba(255,255,255,0.22)",
  borderRadius: 999,
  padding: "8px 13px",
  fontSize: 12,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 14,
};

const heroTitleStyle = {
  fontSize: "clamp(30px, 4vw, 48px)",
  fontWeight: 950,
  letterSpacing: -1.4,
  margin: 0,
};

const heroDescriptionStyle = {
  maxWidth: 760,
  margin: "10px 0 0",
  color: "rgba(255,255,255,0.82)",
  fontSize: 15,
  lineHeight: 1.7,
};

const layoutGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.35fr) minmax(320px, 0.65fr)",
  gap: 22,
  alignItems: "start",
};

const sectionCardStyle = {
  border: "1px solid #dbeafe",
  borderRadius: 22,
  boxShadow: "0 16px 40px rgba(11, 76, 156, 0.08)",
  overflow: "hidden",
  marginBottom: 22,
  background: "#fff",
};

const sectionHeaderStyle = {
  background: "#fff",
  borderBottom: "1px solid #dbeafe",
  padding: "20px 24px",
};

const sectionSubtitleStyle = {
  marginTop: 4,
  fontSize: 13,
  color: "#64748b",
  lineHeight: 1.5,
};

const badgeStyle = {
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#0B4C9C",
  borderRadius: 999,
  padding: "7px 12px",
  fontSize: 12,
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const settingRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 20,
  padding: "18px 0",
  borderBottom: "1px solid #eff6ff",
};

const settingDescriptionStyle = {
  marginTop: 5,
  fontSize: 13,
  color: "#64748b",
  lineHeight: 1.55,
};

const twoColumnGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 18,
  marginBottom: 22,
};

const labelStyle = {
  fontWeight: 800,
  color: "#334155",
};

const inputStyle = {
  borderRadius: 14,
  minHeight: 46,
  borderColor: "#bfdbfe",
};

const primaryButtonStyle = {
  backgroundColor: "#0B4C9C",
  border: "none",
  borderRadius: 14,
  fontWeight: 800,
  padding: "11px 18px",
};

const successButtonStyle = {
  backgroundColor: "#0B4C9C",
  border: "none",
  borderRadius: 14,
  fontWeight: 800,
  minHeight: 46,
  padding: "10px 16px",
};

const tableWrapStyle = {
  border: "1px solid #dbeafe",
  borderRadius: 18,
  overflow: "hidden",
};

const tableHeadStyle = {
  color: "#0B4C9C",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
};

const mcFormGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(260px, 1fr) auto",
  gap: 12,
  alignItems: "end",
  marginTop: 14,
};

const savedBoxStyle = {
  marginTop: 14,
  background: "#f8fafc",
  border: "1px solid #dbeafe",
  borderRadius: 14,
  padding: 14,
  color: "#334155",
  fontSize: 13,
  fontWeight: 600,
};